// End-of-game modal: status, answer, stats, countdown, action buttons.
// Kept as a standalone module so main.ts stays thin.

import {
  loadStats,
  msUntilNextDay,
  formatCountdown,
  type Stats
} from '../game/stats';

interface ResultModalOptions {
  win: boolean;
  answer: string | null;
  attempts: number;
  stats: Stats; // stats AFTER this game was recorded
  onPractice?: () => void;
  onShare?: () => void;
}

function pct(n: number, d: number): string {
  if (!d) return '0%';
  return Math.round((n / d) * 100) + '%';
}

export function showResultModal(opts: ResultModalOptions): void {
  // Remove any previous overlay
  document.querySelector('.wdv-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'wdv-overlay';

  const modal = document.createElement('div');
  modal.className = 'wdv-modal wdv-result';
  overlay.appendChild(modal);

  // --- Title ---
  const title = document.createElement('div');
  title.className = 'wdv-modal-title';
  title.textContent = opts.win ? 'Победа' : 'Не в этот раз';
  modal.appendChild(title);

  // --- Subtitle: answer + attempts info ---
  const sub = document.createElement('div');
  sub.className = 'wdv-modal-sub';
  if (opts.win) {
    sub.textContent = `за ${opts.attempts} ${opts.attempts === 1 ? 'попытку' : opts.attempts < 5 ? 'попытки' : 'попыток'}`;
  } else {
    sub.textContent = 'слово дня';
  }
  modal.appendChild(sub);

  // --- Answer plaque ---
  if (opts.answer) {
    const plaque = document.createElement('div');
    plaque.className = 'wdv-answer';
    plaque.textContent = opts.answer;
    modal.appendChild(plaque);
  }

  // --- Stats row ---
  const statsRow = document.createElement('div');
  statsRow.className = 'wdv-stats-row';
  const s = opts.stats;
  const cells: [string, string][] = [
    [String(s.played), 'игр'],
    [pct(s.won, s.played), 'побед'],
    [String(s.currentStreak), 'серия'],
    [String(s.bestStreak), 'рекорд']
  ];
  for (const [value, label] of cells) {
    const box = document.createElement('div');
    box.className = 'wdv-stat';
    const v = document.createElement('div');
    v.className = 'wdv-stat-value';
    v.textContent = value;
    const l = document.createElement('div');
    l.className = 'wdv-stat-label';
    l.textContent = label;
    box.append(v, l);
    statsRow.appendChild(box);
  }
  modal.appendChild(statsRow);

  // --- Guess distribution (only show if player has any wins) ---
  if (s.won > 0) {
    const distWrap = document.createElement('div');
    distWrap.className = 'wdv-dist';
    const distLabel = document.createElement('div');
    distLabel.className = 'wdv-dist-label';
    distLabel.textContent = 'распределение побед';
    distWrap.appendChild(distLabel);
    const max = Math.max(1, ...s.guessDistribution);
    for (let i = 0; i < 6; i++) {
      const row = document.createElement('div');
      row.className = 'wdv-dist-row';
      const num = document.createElement('div');
      num.className = 'wdv-dist-num';
      num.textContent = String(i + 1);
      const bar = document.createElement('div');
      bar.className = 'wdv-dist-bar';
      const fill = document.createElement('div');
      fill.className = 'wdv-dist-fill';
      const count = s.guessDistribution[i];
      fill.style.width = `${Math.max(6, (count / max) * 100)}%`;
      if (opts.win && opts.attempts === i + 1) fill.classList.add('hl');
      const val = document.createElement('div');
      val.className = 'wdv-dist-val';
      val.textContent = String(count);
      bar.append(fill, val);
      row.append(num, bar);
      distWrap.appendChild(row);
    }
    modal.appendChild(distWrap);
  }

  // --- Countdown to next puzzle ---
  const countdownWrap = document.createElement('div');
  countdownWrap.className = 'wdv-countdown';
  const cdLabel = document.createElement('div');
  cdLabel.className = 'wdv-countdown-label';
  cdLabel.textContent = 'следующее слово через';
  const cdValue = document.createElement('div');
  cdValue.className = 'wdv-countdown-value';
  countdownWrap.append(cdLabel, cdValue);
  modal.appendChild(countdownWrap);

  const tick = () => {
    cdValue.textContent = formatCountdown(msUntilNextDay());
  };
  tick();
  const timer = window.setInterval(tick, 1000);

  // --- Actions ---
  const actions = document.createElement('div');
  actions.className = 'wdv-actions';

  const shareBtn = document.createElement('button');
  shareBtn.className = 'wdv-btn wdv-btn-primary';
  shareBtn.textContent = 'поделиться';
  shareBtn.disabled = true;
  shareBtn.title = 'скоро';
  shareBtn.addEventListener('click', () => opts.onShare?.());

  const practiceBtn = document.createElement('button');
  practiceBtn.className = 'wdv-btn wdv-btn-ghost';
  practiceBtn.textContent = 'практика';
  practiceBtn.disabled = true;
  practiceBtn.title = 'скоро';
  practiceBtn.addEventListener('click', () => opts.onPractice?.());

  actions.append(shareBtn, practiceBtn);
  modal.appendChild(actions);

  // --- Dismiss ---
  const closeBtn = document.createElement('button');
  closeBtn.className = 'wdv-modal-close';
  closeBtn.textContent = 'закрыть';
  closeBtn.addEventListener('click', () => {
    window.clearInterval(timer);
    overlay.remove();
  });
  modal.appendChild(closeBtn);

  // Tap backdrop to close
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      window.clearInterval(timer);
      overlay.remove();
    }
  });

  document.body.appendChild(overlay);
}

/** Convenience: load stats without showing a modal. */
export { loadStats };
